import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { showToast } from '@/shared/utils/core/fetchWrapper';
import logger from '@/shared/utils/core/logger';

/**
 * Full-screen email verification screen.
 * Shows after onboarding completes for email-signup users.
 * Auto-detects when the user clicks the verification link and advances silently.
 */
const EmailVerificationScreen = ({ onSkip }) => {
  const { user, resendEmailVerification, refreshUser, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const pollingRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  // Auto-detect verification — poll every 3 s.
  // When the user clicks the link in their email, refreshUser() will update
  // user.emailVerified to true, causing _layout.jsx to unmount this screen automatically.
  useEffect(() => {
    pollingRef.current = setInterval(async () => {
      try {
        await refreshUser();
        // No explicit action needed: once emailVerified flips to true the
        // parent layout condition fails and this component unmounts.
      } catch {
        // Network blip — just wait for the next interval
      }
    }, 3000);

    return () => {
      clearInterval(pollingRef.current);
      clearTimeout(cooldownTimerRef.current);
    };
  }, [refreshUser]);

  const handleResendVerification = async () => {
    if (cooldown || resending) return;
    try {
      setResending(true);
      await resendEmailVerification();
      showToast('Verification email sent! Check your inbox.', 'success');
      setCooldown(true);
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => setCooldown(false), 60000);
    } catch (error) {
      logger.error('Failed to resend verification:', error);
      if (error.code === 'auth/too-many-requests') {
        showToast('Too many requests. Please wait a few minutes.', 'error');
        setCooldown(true);
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = setTimeout(() => setCooldown(false), 300000);
      } else {
        showToast('Failed to send verification email. Please try again.', 'error');
      }
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (checking) return;
    try {
      setChecking(true);
      await refreshUser();
      showToast('Still waiting for verification. Check your email!', 'info');
    } catch (error) {
      logger.error('Failed to refresh user:', error);
      showToast('Could not check verification status. Please try again.', 'error');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Logout error:', error);
      showToast('Failed to sign out', 'error');
    }
  };

  return (
    <div
      className="min-h-dvh flex items-center justify-center p-4 sm:p-6"
      style={{
        background: 'linear-gradient(160deg, #fffaf6 0%, #fff8f2 60%, #fffaf6 100%)',
        paddingTop: 'max(1.5rem, env(safe-area-inset-top, 1.5rem))',
        paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))',
      }}
      role="main"
      aria-label="Email verification page"
    >
      {/* Ambient bloom */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div style={{
          position: 'absolute',
          bottom: '-140px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '640px',
          height: '420px',
          background: 'radial-gradient(ellipse, rgba(255,184,94,0.16) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-80px',
          width: '380px',
          height: '380px',
          background: 'radial-gradient(circle, rgba(255,158,94,0.08) 0%, transparent 70%)',
        }} />
      </div>

      <div className="relative w-full max-w-md mx-auto">

        {/* Brand wordmark */}
        <div className="text-center mb-12 auth-fade-in">
          <h1 style={{
            fontFamily: "'Libre Baskerville', ui-serif, Georgia, serif",
            fontSize: 'clamp(2.8rem, 8vw, 3.5rem)',
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: '#1a1410',
            margin: '0 0 10px',
          }}>
            Clarity
          </h1>
          <p style={{
            fontSize: '14px',
            fontWeight: 300,
            color: 'rgba(26,20,16,0.4)',
            letterSpacing: '0.03em',
          }}>
            Reflect. Understand. Grow.
          </p>
        </div>

        {/* Verification card */}
        <div
          className="auth-slide-up"
          style={{
            background: 'rgba(255, 252, 248, 0.9)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderRadius: '24px',
            border: '1px solid rgba(26, 20, 16, 0.06)',
            boxShadow: '0 8px 40px rgba(26, 20, 16, 0.07), 0 1px 0 rgba(255,255,255,0.7) inset',
            padding: '32px 28px 36px',
          }}
          role="region"
          aria-label="Email verification status"
        >
          {/* Heading */}
          <h2 style={{
            fontFamily: "'Libre Baskerville', ui-serif, Georgia, serif",
            fontSize: '1.4rem',
            fontWeight: 400,
            lineHeight: 1.3,
            color: '#1a1410',
            textAlign: 'center',
            marginBottom: '8px',
          }}>
            Check your email.
          </h2>
          <p style={{
            fontSize: '14px',
            fontWeight: 300,
            color: 'rgba(26,20,16,0.5)',
            textAlign: 'center',
            lineHeight: 1.6,
            marginBottom: '24px',
          }}>
            We sent a verification link to
          </p>

          {/* Email address pill */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '8px 18px',
                background: '#faf8f5',
                border: '1px solid rgba(26,20,16,0.1)',
                borderRadius: '100px',
                fontSize: '14px',
                fontWeight: 400,
                color: '#1a1410',
                letterSpacing: '0.01em',
              }}
              aria-label={`Verification sent to ${user?.email}`}
            >
              {user?.email}
            </span>
          </div>

          {/* Instruction note */}
          <p style={{
            fontSize: '13px',
            fontWeight: 300,
            color: 'rgba(26,20,16,0.4)',
            textAlign: 'center',
            lineHeight: 1.65,
            marginBottom: '28px',
          }}>
            Click the link in the email — this page will update automatically.
            <br />
            Can't find it? Check your spam folder.
          </p>

          {/* Actions */}
          <div className="space-y-3" role="group" aria-label="Verification actions">

            {/* Primary — manual check (auto-polling handles most cases) */}
            <button
              onClick={handleCheckVerification}
              disabled={checking}
              aria-label={checking ? 'Checking verification status, please wait' : 'Manually check if email has been verified'}
              aria-busy={checking}
              style={{
                width: '100%',
                padding: '14px 24px',
                fontSize: '15px',
                fontWeight: 500,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #ff9e5e 0%, #ffb85e 100%)',
                border: 'none',
                borderRadius: '100px',
                cursor: checking ? 'not-allowed' : 'pointer',
                opacity: checking ? 0.5 : 1,
                boxShadow: '0 4px 20px rgba(255,158,94,0.3)',
                transition: 'box-shadow 0.2s ease, opacity 0.2s ease',
              }}
              onMouseEnter={e => { if (!checking) e.currentTarget.style.boxShadow = '0 6px 28px rgba(255,158,94,0.42)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(255,158,94,0.3)'; }}
            >
              {checking ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Checking...</span>
                </span>
              ) : (
                "I've verified my email →"
              )}
            </button>

            {/* Secondary — resend */}
            <button
              onClick={handleResendVerification}
              disabled={resending || cooldown}
              aria-label={resending ? 'Sending verification email, please wait' : (cooldown ? 'Verification email sent, please check your inbox' : 'Resend verification email')}
              aria-busy={resending}
              aria-disabled={cooldown}
              style={{
                width: '100%',
                padding: '13px 24px',
                fontSize: '14px',
                fontWeight: 400,
                color: cooldown ? 'rgba(26,20,16,0.35)' : 'rgba(26,20,16,0.7)',
                background: '#ffffff',
                border: '1px solid rgba(26,20,16,0.1)',
                borderRadius: '100px',
                cursor: (resending || cooldown) ? 'not-allowed' : 'pointer',
                opacity: (resending || cooldown) ? 0.6 : 1,
                boxShadow: '0 1px 3px rgba(26,20,16,0.05)',
                transition: 'opacity 0.2s ease',
              }}
            >
              {resending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Sending...</span>
                </span>
              ) : cooldown ? (
                'Email sent — check your inbox'
              ) : (
                'Resend verification email'
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative flex items-center my-6">
            <div className="flex-grow" style={{ borderTop: '1px solid rgba(26,20,16,0.07)' }} />
            <span className="flex-shrink mx-4" style={{ fontSize: '11px', letterSpacing: '0.04em', color: 'rgba(26,20,16,0.28)' }}>
              or
            </span>
            <div className="flex-grow" style={{ borderTop: '1px solid rgba(26,20,16,0.07)' }} />
          </div>

          {/* Sign out */}
          <p style={{ textAlign: 'center', fontSize: '13px', fontWeight: 300, color: 'rgba(26,20,16,0.4)' }}>
            Wrong email?{' '}
            <button
              onClick={handleLogout}
              aria-label="Sign out and use a different email address"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 400,
                color: '#ff9e5e',
              }}
            >
              Sign out
            </button>
            {' '}and try again.
          </p>

          {/* Continue anyway — for existing users during transition */}
          {onSkip && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button
                onClick={onSkip}
                aria-label="Skip email verification and continue to the app (not recommended for security)"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 300,
                  color: 'rgba(26,20,16,0.28)',
                }}
              >
                Continue without verifying (not recommended)
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p style={{ fontSize: '12px', fontWeight: 300, color: 'rgba(26,20,16,0.32)', lineHeight: 1.6 }}>
            Having trouble?{' '}
            <a
              href="mailto:support@clarityjournal.co"
              style={{ color: 'rgba(26,20,16,0.48)', textDecoration: 'none', borderBottom: '1px solid rgba(26,20,16,0.15)' }}
              aria-label="Email Clarity support"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes auth-fade-in {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes auth-slide-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .auth-fade-in  { animation: auth-fade-in  0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .auth-slide-up { animation: auth-slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.08s both; }
      `}</style>
    </div>
  );
};

export default EmailVerificationScreen;
